import { useCallback } from "react";

import { Asset, File } from "@/components/graphql/schema.graphql";
import { copyImages, INVALID_ITEM_TYPE_ERR } from "@/features/forge/utils";

import useShowSnackbar from "../useShowSnackbar";

type Copyable =
  | ({ __typename: "File" } & Pick<File, "url" | "contentType">)
  | ({ __typename: "Asset" } & Pick<Asset, "previewUrl">);

export const useCopyFilesToClipboard = () => {
  const { showSuccess, showError } = useShowSnackbar();
  const copyFilesToClipboard = useCallback(
    async (copyable: Copyable[]) => {
      const images = copyable
        .map<Pick<File, "url" | "contentType"> | null>((cp) => {
          if (cp.__typename === "File") {
            return cp;
          }

          if (!cp.previewUrl) {
            return null;
          }

          return {
            // The preview URL should always be up-to-date, so we can use it instead of compiling the image from the Canvas directly.
            url: cp.previewUrl,
            // Will fetch the resource first to get the content-type from the HTTP header
            contentType: "",
          };
        })
        .filter(
          (img): img is Pick<File, "url" | "contentType"> => img !== null,
        );
      const numFiles = images.length;

      try {
        await copyImages(images);
        const successMessage =
          numFiles > 1
            ? `${numFiles} items copied to clipboard.`
            : "Item copied to clipboard!";
        showSuccess(successMessage);
      } catch (e) {
        let errorMessage =
          numFiles > 1
            ? "Error copying items to clipboard"
            : "Error copying item to clipboard";
        if (e instanceof Error) {
          if (e.name === "NotAllowedError") {
            if (
              e.message.includes(
                "Support for multiple ClipboardItems is not implemented.",
              )
            ) {
              errorMessage =
                "Browser does not support copying multiple clipboard items";
            }
          } else if (e.message === INVALID_ITEM_TYPE_ERR) {
            errorMessage =
              "This type of item cannot be copied to the clipboard";
          }
        }
        console.error(errorMessage);
        showError(errorMessage);
      }
    },
    [showSuccess, showError],
  );

  return { copyFilesToClipboard };
};
